import { Entity } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Toggle } from '@design/components/toggle/Toggle';
import { CustomFilterModal } from '@terminal/components/custom-filter-modal/CustomFilterModal';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useEarningsData } from '@terminal/react-query-hooks/earnings/useEarningsData';
import { queryKeys } from '@terminal/routes/query-params';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { EarningsFilters } from './components/earnings-filters/EarningsFilters';
import { EarningsManualEditOptions } from './components/earnings-manual-edit-options/EarningsManualEditOptions';
import { EarningsPriceMovementsChartAccordion } from './components/earnings-price-movements-chart-accordion/EarningsPriceMovementsChartAccordion';
import { EarningsTableWrapper } from './components/earnings-table-wrapper/EarningsTableWrapper';
import styles from './Earnings.module.scss';
import { UnreportedEarnings } from './UnreportedEarnings';
import { useEarningsStore } from './useEarningsStore/useEarningsStore';
import { getEarningsFilterPayload, getEarningsFilters } from './utils/utils';

export type EarningsProps = {
  entity: Entity;
};

export const Earnings = ({ entity }: EarningsProps) => {
  const storeInitializedRef = useRef(false);

  const { isFetching, data, isError, isRefetchError, error } = useEarningsData(
    entity.tag
  );
  const { t } = useTranslation([
    'widget',
    'asset-overview',
    'chat',
    'common',
    'screener',
    'scenario',
    'earnings',
    'analyze',
    'stream',
  ]);
  const {
    initStore,
    isViewSelectedOnly,
    rowSelection,
    rowSelectionByManualEdit,
    setIsViewSelectedOnly,
    earningsData,
    filterGroups,
    onFilterGroupsChange,
    activeModalFilter,
    setActiveModalFilter,
  } = useEarningsStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const { addFilterOption, addActiveFilters } = useFilterActions({
    store: useEarningsStore,
    group: 'EARNINGS_FILTERS',
    onFilterGroupsChange,
  });

  const hasManualChanges = !!Object.keys(rowSelectionByManualEdit).length;

  useEffect(() => {
    if (data && data.earnings !== earningsData) {
      initStore({
        data: data.earnings,
        earningExchange: data.earningExchange,
        filters: getEarningsFilters(t),
      });
      storeInitializedRef.current = true;
    }
  }, [data, earningsData]);

  useEffect(() => {
    const epsFilter = searchParams.get(queryKeys.epsFilter);

    if (
      storeInitializedRef.current &&
      filterGroups.EARNINGS_FILTERS &&
      epsFilter
    ) {
      const epsValue = searchParams.get(queryKeys.epsValue);
      const filterPayload = getEarningsFilterPayload(
        t,
        epsFilter,
        epsValue ? Number(epsValue) : undefined
      );

      if (filterPayload) {
        addFilterOption(filterPayload);
        setIsViewSelectedOnly(false);

        searchParams.delete(queryKeys.epsFilter);
        searchParams.delete(queryKeys.epsValue);
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, filterGroups]);

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '1280px',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="earnings-loading"
      />
    );
  }

  const latest = data?.earnings?.[0];
  const hasEarnings = !!earningsData?.length;

  if (!hasEarnings) {
    return (
      <div className={styles.emptyRoot}>
        <ErrorMessageWrapper
          variant="empty"
          displayIcon
          title={t('widget:earnings.empty.title')}
          subTitle={t('widget:earnings.empty.subTitle', { asset: entity.tag })}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <UnreportedEarnings latest={latest} />
      <div className={styles.filterOptionsWrapper}>
        <Toggle
          className={styles.styledToggle}
          data-testid="view-selected-only-toggle"
          icon="Checkmark"
          isChecked={isViewSelectedOnly}
          onToggle={() => setIsViewSelectedOnly(!isViewSelectedOnly)}
          size="small"
          leftLabel={t('widget:earnings.viewSelectedOnly')}
        />
        <EarningsFilters />
      </div>
      {hasManualChanges && <EarningsManualEditOptions />}
      {!!Object.keys(rowSelection).length && (
        <ErrorBoundaryWrapper>
          <EarningsPriceMovementsChartAccordion
            rowSelection={rowSelection}
            entity={entity}
          />
        </ErrorBoundaryWrapper>
      )}
      <ErrorBoundaryWrapper showBorder={false}>
        <EarningsTableWrapper
          isError={isError}
          isRefetchError={isRefetchError}
          entity={entity}
          error={error}
        />
      </ErrorBoundaryWrapper>
      {activeModalFilter && (
        <CustomFilterModal
          onClose={() => setActiveModalFilter(null)}
          activeFilter={activeModalFilter}
          addFilterOption={addFilterOption}
          addActiveFilters={addActiveFilters}
        />
      )}
    </div>
  );
};
